import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

interface GotifyMessage {
  id: number
  appid: number
  message: string
  title: string
  priority: number
  date: string
  extras?: Record<string, unknown>
}

interface UseGotifyWebSocketProps {
  serverUrl: string
  token: string
  onMessage?: (msg: GotifyMessage) => void
}

export const useGotifyWebSocket = ({ serverUrl, token, onMessage }: UseGotifyWebSocketProps) => {
  const wsRef = useRef<WebSocket | null>(null)
  const [connected, setConnected] = useState(false)
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default')
  const lastMessageIdRef = useRef<number>(0)

  const requestNotificationPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.warn('Este navegador no soporta notificaciones')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission === 'denied') {
      console.warn('Las notificaciones están bloqueadas')
      return false
    }

    try {
      const permission = await Notification.requestPermission()
      setNotificationPermission(permission)
      return permission === 'granted'
    } catch (error) {
      console.error('Error al solicitar permisos de notificación:', error)
      return false
    }
  }

  const showPushNotification = async (message: GotifyMessage) => {
    try {
      const hasPermission = await requestNotificationPermission()

      // Mostrar notificación del sistema si se permite
      if (hasPermission) {
        const preview =
          message.message.length > 100 ? message.message.substring(0, 97) + '...' : message.message

        const notification = new Notification(message.title || 'Nuevo mensaje', {
          body: preview,
          // icon: '/favicon.ico',
          // badge: '/favicon.ico',
          tag: `gotify-${message.id}`,
          data: {
            messageId: message.id,
            fullMessage: message.message,
          },
          requireInteraction: message.priority >= 5,
          silent: message.priority <= 2,
        })

        notification.onclick = (event) => {
          event.preventDefault()
          window.focus()
          notification.close()
        }

        setTimeout(() => {
          notification.close()
        }, 10000)
      }

      // Mostrar toast en la UI siempre
      toast.info(`${message.title || 'Nuevo mensaje'}: ${message.message}`, {
        position: 'top-right',
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
      })
    } catch (error) {
      console.error('Error al mostrar notificación:', error)
    }
  }

  useEffect(() => {
    // Verificar soporte para notificaciones al montar el componente
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission)
    }
  }, [])

  useEffect(() => {
    if (!token) {
      console.warn('Token no disponible. Se cancela conexión WebSocket.')
      return
    }

    let wsProtocol = 'ws'
    if (serverUrl.startsWith('https://')) {
      wsProtocol = 'wss'
    } else if (serverUrl.startsWith('http://')) {
      wsProtocol = 'ws'
    } else {
      console.error('serverUrl inválido. Debe comenzar con http:// o https://')
      return
    }

    const cleanedUrl = serverUrl.replace(/^https?:\/\//, '')
    const wsUrl = `${wsProtocol}://${cleanedUrl}/stream?token=${token}`

    console.log('Conectando a WebSocket con URL:', wsUrl)

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      console.log('WebSocket conectado')
      setConnected(true)
    }

    ws.onmessage = (event) => {
      try {
        const msg: GotifyMessage = JSON.parse(event.data)
        if (msg.id > lastMessageIdRef.current) {
          lastMessageIdRef.current = msg.id
          if (onMessage) onMessage(msg)
          showPushNotification(msg)
        }
      } catch (err) {
        console.error('Error al procesar mensaje:', err)
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setConnected(false)
    }

    ws.onclose = (event) => {
      console.log('WebSocket cerrado:', event.code, event.reason)
      setConnected(false)
    }

    return () => {
      console.log('WebSocket limpiado')
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close()
      }
    }
  }, [serverUrl, token])

  // Función helper para solicitar permisos manualmente
  const requestPermissions = async () => {
    return await requestNotificationPermission()
  }

  return {
    connected,
    notificationPermission,
    requestPermissions,
  }
}
